import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Loader2, ShieldAlert } from "lucide-react";
import { http, errorMessage } from "../lib/api";
import { Logo } from "../components/site/Header";
import AdminDashboard from "./AdminDashboard";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const login = () => {
  const redirectUrl = window.location.origin + "/admin";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const Shell = ({ children }) => (
  <div className="flex min-h-screen items-center justify-center bg-[#F4F7FB] px-5">
    <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-10 text-center shadow-[0_20px_50px_rgba(16,43,80,0.08)]">
      <Logo className="mx-auto h-10" />
      {children}
    </div>
  </div>
);

export const AuthCallback = () => {
  const navigate = useNavigate();
  const done = useRef(false);
  const [error, setError] = useState("");
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const sid = new URLSearchParams(window.location.hash.slice(1)).get("session_id");
    window.history.replaceState(null, "", "/admin");
    http.post("/auth/session", {}, { headers: { "X-Session-ID": sid } })
      .then((r) => navigate("/admin", { replace: true, state: { user: r.data } }))
      .catch((e) => setError(errorMessage(e, "Sign-in failed. Please try again.")));
  }, [navigate]);
  if (!error) return <Shell><Loader2 className="mx-auto mt-8 h-6 w-6 animate-spin text-[#2878E5]" /></Shell>;
  return (
    <Shell>
      <ShieldAlert className="mx-auto mt-8 h-8 w-8 text-red-500" />
      <p data-testid="admin-auth-error" className="mt-4 text-sm text-[#2D3F58]">{error}</p>
      <button onClick={() => navigate("/admin", { replace: true })} className="mt-6 text-sm font-semibold text-[#2878E5]">Back</button>
    </Shell>
  );
};

export default function Admin() {
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user ?? null);
  const [checked, setChecked] = useState(!!location.state?.user);

  useEffect(() => {
    document.title = "Admin | AppTarget";
    if (location.state?.user) return;
    http.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(null)).finally(() => setChecked(true));
  }, [location.state]);

  if (!checked) return <Shell><Loader2 className="mx-auto mt-8 h-6 w-6 animate-spin text-[#2878E5]" /></Shell>;
  if (user) return <AdminDashboard user={user} onLogout={() => setUser(null)} />;
  return (
    <Shell>
      <h1 className="mt-8 font-display text-2xl font-bold text-[#102B50]">Admin Dashboard</h1>
      <p className="mt-2 text-sm text-[#5A6D88]">Sign in with an authorised Google account to view enquiries.</p>
      <button onClick={login} data-testid="admin-google-login-btn" className="mt-8 inline-flex w-full items-center justify-center gap-3 rounded-full bg-[#102B50] px-6 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-[#0A1E3A]">
        <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden><path fill="#fff" d="M21.35 11.1H12v2.98h5.35c-.23 1.5-1.68 4.4-5.35 4.4-3.22 0-5.85-2.67-5.85-5.96S8.78 6.56 12 6.56c1.83 0 3.06.78 3.76 1.45l2.57-2.47C16.7 4.03 14.56 3 12 3 7.03 3 3 7.03 3 12s4.03 9 9 9c5.2 0 8.64-3.65 8.64-8.8 0-.59-.06-1.04-.14-1.5z" /></svg>
        Sign in with Google
      </button>
    </Shell>
  );
}
